import useResizeObserver from '@react-hook/resize-observer'
import React, { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { breakPoints } from '../utils/config'
import { getGoogleMapsLink, Icon, ResponsiveImage } from '@bm-js/atlascloud-ui'
import {
  calculateForeground,
  lightenDarkenColor,
  LucideIconName,
  WebsiteConfigBM,
} from '@bm-js/atlascloud-shared'

const StyledFooter1 = styled.div<{ $baseColor: string }>`
  background: ${({ $baseColor }) => $baseColor};
  color: ${({ $baseColor }) => calculateForeground($baseColor)};
  .footer-top {
    .footer-inner {
      .logo {
        img {
          max-width: 150px;
          max-height: 100px;
          object-fit: contain;
          object-position: left;
        }
      }
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1rem;
      padding-top: 3.5rem;
      padding-bottom: 10rem;
      h4 {
        font-size: 20px;
        line-height: 36px;
        font-weight: 600;
      }
      p {
        font-size: 14px;
        line-height: 20px;
        font-weight: 500;
      }
      svg {
        margin-top: 0.5rem;
      }
    }
  }
  .footer-bottom {
    background: ${({ $baseColor }) => lightenDarkenColor($baseColor, -20)};
    color: ${({ $baseColor }) =>
      calculateForeground(lightenDarkenColor($baseColor, -20))};
    padding: 1rem;
    font-size: 0.9rem;
    text-align: center;
  }
  .so-me {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  &.tablet {
    .footer-top {
      .footer-inner {
        grid-template-columns: 1fr;
      }
    }
  }
`

type Props = {
  websiteConfig: WebsiteConfigBM
  optOutFromAtlasCloudAttribution?: boolean
}

const Footer1 = ({ websiteConfig, optOutFromAtlasCloudAttribution }: Props) => {
  const [width, setWidth] = useState(0)
  const target = useRef(null)
  useResizeObserver(target, (entry) => {
    setWidth(entry.contentRect.width)
  })

  const googleMapsLink = useMemo(() => {
    return getGoogleMapsLink(websiteConfig.contactInfo)
  }, [websiteConfig])

  const hasAddress = useMemo(() => {
    const { adress, postalCode, postalArea } = websiteConfig.contactInfo || {}
    return adress || (postalCode && postalArea)
  }, [websiteConfig.contactInfo])

  return (
    <StyledFooter1
      $baseColor={websiteConfig.colors.base}
      ref={target}
      className={`${width < breakPoints.mobile ? 'mobile' : ''} ${
        width < breakPoints.tablet ? 'tablet' : ''
      } `}
    >
      <div className='footer-top'>
        <div className='footer-inner content'>
          {websiteConfig.logoWide && (
            <div className='footer-col'>
              <div className='logo'>
                <ResponsiveImage file={websiteConfig.logoWide} />
              </div>
            </div>
          )}
          {hasAddress && (
            <div className='footer-col'>
              <h4 className='m0'>Adress</h4>
              <p className='m0'>
                {websiteConfig.contactInfo?.recipient
                  ?.split('\n')
                  .map((p, i) => (
                    <React.Fragment key={`${p}_${i}`}>
                      {p}
                      <br />
                    </React.Fragment>
                  ))}
                {websiteConfig.contactInfo?.adress?.split('\n').map((p, i) => (
                  <React.Fragment key={`${p}_${i}`}>
                    {p}
                    <br />
                  </React.Fragment>
                ))}
                {`${websiteConfig.contactInfo?.postalCode} ${websiteConfig.contactInfo?.postalArea}`}
              </p>
              {googleMapsLink && (
                <p className='mtop05'>
                  <a href={googleMapsLink} target='_blank' rel='noreferrer'>
                    Öppna i Google Maps
                  </a>
                </p>
              )}
            </div>
          )}
          <div className='footer-col'>
            <h4 className='m0'>Kontakt</h4>
            <p className='m0'>
              <a href={`mailto:${websiteConfig.contactInfo?.email}`}>
                {websiteConfig.contactInfo?.email}
              </a>
              <br />
              <a href={`tel:${websiteConfig.contactInfo?.phone}`}>
                {websiteConfig.contactInfo?.phone}
              </a>
            </p>
          </div>
          <div className='footer-col'>
            {!!websiteConfig.socialMedia?.instagram &&
              !!websiteConfig.socialMedia?.facebook && (
                <h4 className='m0'>Sociala medier</h4>
              )}
            <div className='so-me'>
              {!!websiteConfig.socialMedia?.instagram && (
                <a
                  href={websiteConfig.socialMedia?.instagram}
                  className='text-customerInverse'
                >
                  <Icon
                    size={32}
                    strokeWidth={1.5}
                    name={LucideIconName.Instagram}
                  />
                </a>
              )}
              {!!websiteConfig.socialMedia?.facebook && (
                <a
                  href={websiteConfig.socialMedia?.facebook}
                  className='text-customerInverse'
                >
                  <Icon
                    size={32}
                    strokeWidth={1.5}
                    name={LucideIconName.Facebook}
                  />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
      {!optOutFromAtlasCloudAttribution && (
        <div className='footer-bottom'>
          <p className='m0'>
            Sidan drivs av{' '}
            <a href='https://atlascloud.se' target='_blank' rel='noreferrer'>
              Atlas Cloud
            </a>
          </p>
        </div>
      )}
    </StyledFooter1>
  )
}

export default Footer1
